import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Languages, Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { trackLanguageSwitch, trackOutboundClick } from "./Analytics";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `relative py-1 text-sm font-medium tracking-tight transition-colors hover:text-teal-deep ${
    isActive
      ? "text-teal-deep after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-teal"
      : "text-slate-600 hover:text-navy"
  }`;

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 shadow-premium backdrop-blur-2xl border-b border-slate-100/80"
          : "bg-white/80 backdrop-blur-xl border-b border-transparent"
      }`}
    >
      <div className="container-limit flex items-center justify-between py-3.5">
        {/* Logo */}
        <button onClick={() => navigate("/")} className="flex items-center gap-3 group" aria-label="Trevia Group — home">
          <img src="/trevia-logo.svg" alt="" className="h-11 w-auto" />
          <span className="font-display text-xl font-extrabold text-navy tracking-tight uppercase">
            Trevia Group
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex">
          <NavLink to="/" end className={navLinkClass}>{t.nav.home}</NavLink>
          <NavLink to="/about" className={navLinkClass}>{t.nav.about}</NavLink>
          <NavLink to="/freight" className={navLinkClass}>{t.nav.freight}</NavLink>
          <NavLink to="/sourcing" className={navLinkClass}>{t.nav.sourcing}</NavLink>
          <NavLink to="/catalog" className={navLinkClass}>{t.nav.catalog}</NavLink>
          <NavLink to="/blog" className={navLinkClass}>{t.nav.blog}</NavLink>
          <a href="https://ship-together.com" target="_blank" rel="noopener noreferrer" onClick={() => trackOutboundClick("https://ship-together.com", "ShipTogether")} className="relative py-1 text-sm font-medium tracking-tight transition-colors text-slate-600 hover:text-teal">{t.nav.ship}</a>
          <NavLink to="/contact" className={navLinkClass}>{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200/60 bg-slate-50/80 px-2 py-1 text-xs backdrop-blur-sm">
            <Languages className="mr-0.5 h-3.5 w-3.5 text-slate-400" />
            <button
              onClick={() => { setLang("en"); trackLanguageSwitch("en"); }}
              className={`rounded-full px-2 py-0.5 transition-all duration-200 ${
                lang === "en"
                  ? "gradient-teal font-semibold text-white shadow-sm"
                  : "text-slate-500 hover:text-navy"
              }`}
            >EN</button>
            <button
              onClick={() => { setLang("zh"); trackLanguageSwitch("zh"); }}
              className={`rounded-full px-2 py-0.5 transition-all duration-200 ${
                lang === "zh"
                  ? "gradient-teal font-semibold text-white shadow-sm"
                  : "text-slate-500 hover:text-navy"
              }`}
            >中文</button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen(!open)}
            className="rounded-xl p-2 hover:bg-slate-100/80 lg:hidden transition"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X className="h-5 w-5 text-slate-600" /> : <Menu className="h-5 w-5 text-slate-600" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <nav id="mobile-nav" className="animate-slide-down border-t border-slate-100 bg-white/95 backdrop-blur-xl px-4 pb-4 pt-2 lg:hidden">
          <div className="flex flex-col gap-0.5">
            {[
              { to: "/", label: t.nav.home, end: true },
              { to: "/about", label: t.nav.about },
              { to: "/freight", label: t.nav.freight },
              { to: "/sourcing", label: t.nav.sourcing },
              { to: "/catalog", label: t.nav.catalog },
              { to: "/blog", label: t.nav.blog },
              { to: "/contact", label: t.nav.contact },
            ].map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-teal/10 to-cyan/5 text-teal-deep"
                      : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href="https://ship-together.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all"
            >
              {t.nav.ship}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
